import { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { api } from '@/lib/api';
import { isValidEmail } from '@/lib/email-validation';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

interface SendSubmittalDialogProps {
  submittalId: string;
  submittalNumber: string;
  defaultRecipient?: string;
  /** #91: gate disables Send when both are zero (blank cover sheet guard). */
  itemCount?: number;
  fileCount?: number;
}

export function SendSubmittalDialog({
  submittalId,
  submittalNumber,
  defaultRecipient,
  itemCount = 0,
  fileCount = 0,
}: SendSubmittalDialogProps) {
  const [open, setOpen] = useState(false);
  const [recipient, setRecipient] = useState(defaultRecipient || '');
  const [customNote, setCustomNote] = useState('');
  const [sending, setSending] = useState(false);
  const queryClient = useQueryClient();

  const trimmedRecipient = recipient.trim();
  const recipientEntered = trimmedRecipient.length > 0;
  const recipientValid = recipientEntered && isValidEmail(trimmedRecipient);
  const showRecipientError = recipientEntered && !recipientValid;

  const isEmpty = itemCount === 0 && fileCount === 0;
  const emptyReason = isEmpty
    ? 'Add at least one item or file before sending.'
    : null;

  const canSend = recipientValid && !isEmpty;

  const handleSend = async () => {
    if (!canSend) return;

    setSending(true);
    try {
      await api.submittals.sendEmail(submittalId, {
        recipient: trimmedRecipient,
        custom_note: customNote.trim() || undefined,
      });
      toast.success(`Submittal ${submittalNumber} sent to ${trimmedRecipient}`);
      queryClient.invalidateQueries({ queryKey: ['submittals'] });
      queryClient.invalidateQueries({ queryKey: ['submittal'] });
      setOpen(false);
      setCustomNote('');
    } catch (error: any) {
      toast.error(error.message || 'Failed to send submittal');
    } finally {
      setSending(false);
    }
  };

  // The trigger button itself is disabled with a tooltip when the submittal is
  // blank — that way users see *why* they can't open the dialog without having
  // to open it first.
  const triggerButton = (
    <Button
      variant="outline"
      size="sm"
      className="h-7 text-xs"
      disabled={isEmpty}
      data-testid="send-submittal-trigger"
    >
      <Send className="w-3 h-3 mr-1" />
      Send
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {emptyReason ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* span wrapper is required for tooltip-on-disabled-button */}
              <span tabIndex={0}>{triggerButton}</span>
            </TooltipTrigger>
            <TooltipContent data-testid="send-submittal-disabled-tooltip">
              {emptyReason}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <DialogTrigger asChild>{triggerButton}</DialogTrigger>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send Submittal {submittalNumber}</DialogTitle>
          <DialogDescription>
            The cover sheet PDF and attached files will be sent to the recipient.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="recipient">Recipient Email *</Label>
            <Input
              id="recipient"
              type="email"
              placeholder="architect@example.com"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              aria-invalid={showRecipientError || undefined}
              data-testid="recipient-input"
            />
            {showRecipientError && (
              <p
                className="text-xs text-destructive"
                data-testid="recipient-format-error"
              >
                Enter a valid email address.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="note">Custom Note (optional)</Label>
            <Textarea
              id="note"
              placeholder="Add a personal note..."
              value={customNote}
              onChange={(e) => setCustomNote(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !canSend}
            data-testid="send-submittal-submit"
          >
            {sending ? 'Sending...' : 'Send Submittal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
